<template>
    <div class="relative">
        <div
            class="overflow-y-scroll space-y-1.5"
            style="max-height: 402px"
        >
            <template
                v-for="availabilityResultTimeSlot in availabilityResultDate.availabilityResultTimeSlots"
            >
                <Selectable
                    as="button"
                    type="button"
                    class="block w-full"
                    @update:modelValue="handleTimeSlotSelectionChanged($event)"
                    v-slot="selectable"
                    :value="availabilityResultTimeSlot"
                    :modelValue="selectedAvailabilityResultTimeSlot"
                >
                    <div
                        class="p-3 rounded-lg2 border border-gray-200 ring-inset"
                        :class="getTimeSlotButtonColor(selectable.active)"
                    >
                        <div
                            class="flex flex-col"
                            :class="{
                                'text-gray-700':
                                    !useBookingFormThemeCssVariables,
                            }"
                        >
                            <span class="text-sm font-semibold">
                                {{
                                    useDate().timeslot(
                                        availabilityResultTimeSlot
                                            .timeSlotInSelectedTimeZoneForDisplay
                                            .start,
                                        timeslotTimeFormat,
                                    )
                                }}
                            </span>
                            <span
                                v-if="shouldRenderAvailableSeats"
                                class="mt-0.5 text-sm font-light"
                                :class="{
                                    'text-gray-600':
                                        !useBookingFormThemeCssVariables,
                                }"
                            >
                                {{
                                    getAvailableSeatsLabel(
                                        availabilityResultTimeSlot.numberOfAvailableSeats,
                                    )
                                }}
                            </span>
                        </div>
                    </div>
                </Selectable>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType } from 'vue'
    import Selectable from '@/Shared/Component/Selectable.vue'
    import type {
        AvailabilityResultDateType,
        AvailabilityResultTimeSlotType,
    } from '@/Shared/AutoGenerated/Main'
    import useDate, { TimeslotTimeFormat } from '@/Shared/Hook/useDate'

    const emit = defineEmits<{
        onTimeSlotSelectionChange: [
            { availabilityResultTimeSlot: AvailabilityResultTimeSlotType },
        ]
    }>()

    const props = defineProps({
        availabilityResultDate: {
            type: Object as PropType<AvailabilityResultDateType>,
            required: true,
        },
        shouldRenderAvailableSeats: {
            type: Boolean,
            required: true,
        },
        selectedAvailabilityResultTimeSlot: {
            type: Object as PropType<AvailabilityResultTimeSlotType>,
            default: null,
        },
        timeslotTimeFormat: {
            type: String as PropType<TimeslotTimeFormat>,
            required: true,
        },
        useBookingFormThemeCssVariables: {
            type: Boolean,
            default: false,
        },
    })

    const handleTimeSlotSelectionChanged = (
        selectedAvailabilityResultTimeSlot: AvailabilityResultTimeSlotType | null,
    ) => {
        if (!selectedAvailabilityResultTimeSlot) {
            return
        }

        emit('onTimeSlotSelectionChange', {
            availabilityResultTimeSlot: selectedAvailabilityResultTimeSlot,
        })
    }

    const getAvailableSeatsLabel = (numberOfAvailableSeats: number): string => {
        if (numberOfAvailableSeats === 1) {
            return '1 seat left'
        }

        return `${numberOfAvailableSeats} seats left`
    }

    const getTimeSlotButtonColor = (isSelected: boolean): string => {
        return props.useBookingFormThemeCssVariables
            ? getThemedButtonCssClasses(isSelected)
            : getDefaultButtonCssClasses(isSelected)
    }

    const getDefaultButtonCssClasses = (isSelected: boolean): string => {
        if (isSelected) {
            return [
                'bg-primary-shade-ligth',
                'hover:bg-primay-shade-light',

                'border-transparent',

                'ring-2',
                'ring-primary',
            ].join(' ')
        }

        return [
            'bg-white',

            'hover:border-transparent',

            'hover:ring-2',
            'hover:ring-primary',
        ].join(' ')
    }

    const getThemedButtonCssClasses = (isSelected: boolean): string => {
        if (isSelected) {
            return [
                'bg-booking-form-theme-main-background-shade',

                'text-booking-form-theme-main-text-color',

                'border-transparent',

                'ring-2',
                'ring-booking-form-theme-main-background-color',
            ].join(' ')
        }

        return [
            'bg-white',

            'text-booking-form-theme-main-text-color',

            'hover:border-transparent',

            'hover:ring-2',
            'hover:ring-booking-form-theme-main-background-color',
        ].join(' ')
    }
</script>
